import { computed } from 'mobx'
import { omit } from 'lodash'

export type FormattedSelectOption<T> = Omit<T, 'options'> & {
    label: string
    value: number | string
    opts: any[]
}

/**
 * FORMATS ARRAY OF OBJECTS INTO A USABLE ARRAY
 * FOR A 'react-select' COMPONENT
 *
 * Important note: react-select will break if an items data has the key "options", hence renaming it to `opts` here.
 * https://github.com/JedWatson/react-select/issues/3706
 */
export function formatForSelect<T extends Record<string, any>>(
    array: T[],
    labelKey: string,
    valueKey: string,
    def = 'Name not provided',
    defaultLabelKey?: string
): FormattedSelectOption<T>[] {
    const labelFields = labelKey.split('+')

    if (!array) return []
    if (!Array.isArray(array)) array = [array]
    if (array[0] === null || array[0] === undefined) return []

    return computed(() => array.map((item) => {
        // Handle any number of label fields
        let label = labelFields
            .map(field => item[field.trim()] ?? '')
            .filter(Boolean)
            .join(' ')

        // Convert to string if not undefined/null, otherwise use empty string
        label = label != null ? String(label) : ''
        label = label.trim()

        // Handle empty/invalid labels
        if (!label) {
            label = item[defaultLabelKey] ? String(item[defaultLabelKey]) : def
        }
        if (!label) {
            label = `Unknown:${labelKey}`
        }

        return {
            ...omit(item, ['options']),
            label,
            value: item[valueKey],
            opts: item.options || []
        }
    })).get()
}
